import React, { useState, useEffect, useRef } from 'react';
import { getBackendUrl } from '@/config';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft, FileText, User, DollarSign, Calendar, Eye, MessageSquare, Crown, Zap, Star, Download } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { DocumentPreviewModal } from '@/components/shared/DocumentPreviewModal';
import { SSNRevealButton } from '@/components/shared/SSNRevealButton';
import { pricingAPI } from '@/services/api';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export default function AdminFilingDetailView() {
  const { filingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filing, setFiling] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const messagesEndRef = useRef(null);
  
  // Pricing from API
  const [packagePrices, setPackagePrices] = useState({
    basic: 99,
    standard: 199,
    premium: 349
  });
  
  // Document preview modal state
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    documentId: null,
    fileUrl: null,
    fileName: null,
    mimeType: null,
    isAgentDocument: false
  });
  
  // Helper function to get package price
  const getPackagePrice = (packageName) => {
    if (!packageName) return 0;
    return packagePrices[packageName.toLowerCase()] || 0;
  };

  // Fetch pricing plans
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const plans = await pricingAPI.getPlans('tax', true, false);
        if (plans && plans.length > 0) {
          const prices = {};
          const normalizeKey = (name) => {
            const lowerName = name.toLowerCase();
            if (lowerName.includes('basic')) return 'basic';
            if (lowerName.includes('standard')) return 'standard';
            if (lowerName.includes('premium')) return 'premium';
            if (lowerName.includes('advanced')) return 'advanced';
            return lowerName.split(' ')[0];
          };
          plans.forEach(plan => {
            const key = normalizeKey(plan.name);
            prices[key] = plan.price;
          });
          if (Object.keys(prices).length > 0) {
            setPackagePrices(prev => ({ ...prev, ...prices }));
          }
        }
      } catch (error) {
        console.error('Error fetching pricing:', error);
      }
    };
    fetchPricing();
  }, []);

  useEffect(() => {
    if (user && filingId) {
      fetchFilingDetail();
      fetchDocuments();
      fetchMessages();
    }
  }, [user, filingId]);

  const fetchMessages = async () => {
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${backendUrl}/api/filings/${filingId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(
        `${backendUrl}/api/documents/tax_filing/${filingId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setDocuments(response.data);
    } catch (error) {
      console.error('[AdminFilingDetailView] Error fetching documents:', error);
    }
  };

  const handleViewDocument = async (documentId, fileUrl, fileName, mimeType = null, isAgentDocument = false, isJsonbDocument = false) => {
    const backendUrl = getBackendUrl();
    const token = localStorage.getItem('access_token');
    
    try {
      let viewUrl;
      let fetchOptions = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      if (isAgentDocument) {
        viewUrl = `${backendUrl}/api/files/agent-document/${filingId}/${documentId}`;
      } else if (isJsonbDocument && fileUrl) {
        // JSONB documents - use POST endpoint
        viewUrl = `${backendUrl}/api/files/view-by-url`;
        fetchOptions = {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            file_url: fileUrl,
            entity_type: 'tax_filing',
            entity_id: filingId
          })
        };
      } else if (documentId) {
        viewUrl = `${backendUrl}/api/files/view/${documentId}`;
      } else {
        viewUrl = fileUrl.startsWith('http') ? fileUrl : `${backendUrl}${fileUrl}`;
      }
      
      const response = await fetch(viewUrl, fetchOptions);
      
      if (!response.ok) {
        throw new Error('Failed to load document');
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error('Error viewing document:', error);
      toast.error('Failed to load document');
    }
  };

  const fetchFilingDetail = async () => {
    try {
      setLoading(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(`${backendUrl}/api/filings/${filingId}/detail`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;
      
      // Parse JSON fields if they're strings
      if (typeof data.personal_info === 'string') {
        try {
          data.personal_info = JSON.parse(data.personal_info);
        } catch (e) {
          data.personal_info = {};
        }
      }
      
      if (typeof data.income_details === 'string') {
        try {
          data.income_details = JSON.parse(data.income_details);
        } catch (e) {
          data.income_details = {};
        }
      }
      
      if (typeof data.deductions === 'string') {
        try {
          data.deductions = JSON.parse(data.deductions);
        } catch (e) {
          data.deductions = {};
        }
      }

      setFiling(data);
    } catch (error) {
      console.error('[AdminFilingDetailView] Error fetching filing detail:', error);
      toast.error(error.response?.data?.detail || 'Failed to load filing details');
      setFiling(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    const basePath = user?.role === 'agency' ? '/agency' : '/admin';
    navigate(`${basePath}/filings`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUBMITTED': return 'bg-blue-100 text-blue-700';
      case 'NEED_INFO': return 'bg-yellow-100 text-yellow-700';
      case 'READY_FOR_REVIEW': return 'bg-purple-100 text-purple-700';
      case 'SUBMITTED_TO_IRS': return 'bg-green-100 text-green-700';
      case 'DRAFT': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPackageIcon = (packageName) => {
    switch (packageName?.toLowerCase()) {
      case 'premium': return <Crown className="h-4 w-4 text-amber-600" />;
      case 'standard': return <Zap className="h-4 w-4 text-blue-600" />;
      case 'basic': return <Star className="h-4 w-4 text-slate-600" />;
      default: return null;
    }
  };

  if (!user) return <div>Loading...</div>;

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="Tax Filing Details" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!filing) {
    return (
      <DashboardLayout role={user.role} title="Tax Filing Details" user={user}>
        <div className="text-center py-12">
          <FileText className="h-16 w-16 mx-auto text-muted-foreground" />
          <h3 className="mt-4 text-lg font-semibold">Tax filing not found</h3>
          <Button className="mt-4" onClick={handleBackClick}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Filings
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const selectedPackage = filing.selected_package || filing.personal_info?.selectedPackage || '';
  const isAgency = user?.role === 'agency';
  const basePath = isAgency ? '/agency' : '/admin';
  const roleLabel = isAgency ? 'Agency' : 'Admin';

  return (
    <DashboardLayout role={user.role} title="Tax Filing Details" user={user}>
      <div className="space-y-6">
        <Breadcrumbs items={[
          { label: roleLabel, href: `${basePath}/dashboard` },
          { label: 'Filings', href: `${basePath}/filings` },
          { label: 'Filing Details' }
        ]} />

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Tax Filing Details</h2>
            <p className="text-muted-foreground">{filing.client_name} - {filing.tax_year}</p>
          </div>
          <Button variant="outline" onClick={handleBackClick}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Filings
          </Button>
        </div>

        {/* Client Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Client Information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="text-sm text-muted-foreground">Name</p>
              <p className="font-medium">{filing.client_name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-medium">{filing.client_email || 'N/A'}</p>
            </div>
          </CardContent>
        </Card>

        {/* Filing Info - VIEW ONLY */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Filing Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-4">
              <div>
                <p className="text-sm text-muted-foreground">Tax Year</p>
                <p className="font-semibold text-lg">{filing.tax_year}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <Badge className={cn("mt-1", getStatusColor(filing.status))}>
                  {filing.status?.replace(/_/g, ' ')}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Package</p>
                <div className="flex items-center gap-2 mt-1">
                  {getPackageIcon(selectedPackage)}
                  <span className="font-medium capitalize">{selectedPackage || 'Not selected'}</span>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Payment Status</p>
                {filing.payment_status === 'paid' ? (
                  <Badge className="bg-green-100 text-green-700 hover:bg-green-100 mt-1">
                    <DollarSign className="h-3 w-3 mr-1" />
                    Paid
                    {filing.paid_at && (
                      <span className="ml-1 text-xs">
                        ({new Date(filing.paid_at).toLocaleDateString()})
                      </span>
                    )}
                  </Badge>
                ) : filing.payment_status === 'failed' ? (
                  <Badge className="bg-red-100 text-red-700 hover:bg-red-100 mt-1">
                    Payment Failed
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground mt-1">
                    Pending
                  </Badge>
                )}
              </div>
            </div>

            {/* Financial Summary - VIEW ONLY */}
            <div className="grid gap-4 md:grid-cols-4 p-4 bg-muted/30 rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Package Price</p>
                <p className="font-semibold text-lg">${getPackagePrice(selectedPackage)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Discount</p>
                <p className="font-semibold text-lg text-orange-600">
                  {filing.discount_amount ? `-$${Number(filing.discount_amount).toLocaleString()}` : '$0'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total After Discount</p>
                <p className="font-semibold text-lg text-green-600">
                  ${Math.max(0, getPackagePrice(selectedPackage) - (filing.discount_amount || 0)).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Estimated Refund</p>
                <p className="font-semibold text-lg text-green-600">
                  {filing.estimated_refund ? `$${Number(filing.estimated_refund).toLocaleString()}` : 'N/A'}
                </p>
              </div>
            </div>

            {/* Balance Due if any */}
            {filing.balance_due && (
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-muted-foreground">Balance Due</p>
                <p className="font-semibold text-lg text-red-600">
                  ${Number(filing.balance_due).toLocaleString()}
                </p>
              </div>
            )}

            {/* Messages - VIEW ONLY */}
            {messages.length > 0 && (
              <div className="p-4 border rounded-lg bg-muted/20 space-y-3">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  <span className="font-medium">Conversation History</span>
                  <Badge variant="secondary">{messages.length} messages</Badge>
                </div>
                
                <div className="border rounded-lg bg-white dark:bg-gray-800">
                  <ScrollArea className="h-64 p-3">
                    <div className="space-y-3">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={cn(
                            "flex",
                            msg.sender_role === 'agent' || msg.sender_role === 'admin' ? 'justify-end' : 'justify-start'
                          )}
                        >
                          <div
                            className={cn(
                              "max-w-[80%] rounded-lg p-3",
                              msg.sender_role === 'agent' || msg.sender_role === 'admin'
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted'
                            )}
                          >
                            <p className="text-xs font-medium mb-1 opacity-70">
                              {msg.sender_role === 'agent' ? 'Agent' : msg.sender_role === 'admin' ? 'Admin' : msg.sender_name || 'Client'}
                            </p>
                            <p className="text-sm">{msg.message}</p>
                            <p className="text-xs mt-1 opacity-50">
                              {new Date(msg.created_at).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>
                </div>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm text-muted-foreground">Filing Type</p>
                <p className="font-medium capitalize">{filing.filing_type || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Created</p>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <p className="font-medium">
                    {filing.created_at ? new Date(filing.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last Updated</p>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <p className="font-medium">
                    {filing.updated_at ? new Date(filing.updated_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
              {filing.submission_date && (
                <div>
                  <p className="text-sm text-muted-foreground">Submission Date</p>
                  <p className="font-medium">
                    {new Date(filing.submission_date).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Personal Information */}
        {filing.personal_info && typeof filing.personal_info === 'object' && Object.keys(filing.personal_info).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {/* Primary SSN with reveal button */}
                {filing.personal_info?.ssn_token && (
                  <SSNRevealButton
                    ssnToken={filing.personal_info.ssn_token}
                    maskedSSN={filing.personal_info.ssn || 'XXX-XX-XXXX'}
                    label="SSN"
                  />
                )}
                
                {/* Spouse SSN with reveal button */}
                {filing.personal_info?.spouseSSN_token && (
                  <SSNRevealButton
                    ssnToken={filing.personal_info.spouseSSN_token}
                    maskedSSN={filing.personal_info.spouseSSN || 'XXX-XX-XXXX'}
                    label="Spouse SSN"
                  />
                )}
                
                {Object.entries(filing.personal_info)
                  .filter(([key, value]) => {
                    if (key === 'dependents' || key === 'documents') return false;
                    if (key === 'ssn' || key === 'ssn_token' || key === 'ssn_secured') return false;
                    if (key === 'spouseSSN' || key === 'spouseSSN_token' || key === 'spouseSSN_secured') return false;
                    if (typeof value === 'object' && value !== null) return false;
                    return true;
                  })
                  .map(([key, value]) => {
                    let displayValue = value;
                    if (typeof value === 'boolean') {
                      displayValue = value ? 'Yes' : 'No';
                    }
                    
                    return (
                      <div key={key}>
                        <p className="text-sm text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                        <p className="font-medium">{displayValue || 'N/A'}</p>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Dependents Information */}
        {filing.personal_info?.dependents && Array.isArray(filing.personal_info.dependents) && filing.personal_info.dependents.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Dependents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {filing.personal_info.dependents.map((dependent, index) => (
                  <div key={dependent.id || index} className="p-4 border rounded-lg">
                    <p className="font-semibold mb-2">Dependent {index + 1}</p>
                    <div className="grid gap-2 md:grid-cols-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Name</p>
                        <p className="text-sm font-medium">{dependent.firstName} {dependent.lastName}</p>
                      </div>
                      {dependent.ssn_token ? (
                        <SSNRevealButton
                          ssnToken={dependent.ssn_token}
                          maskedSSN={dependent.ssn || 'XXX-XX-XXXX'}
                          label="SSN/ITIN"
                          className="text-sm"
                        />
                      ) : (
                        <div>
                          <p className="text-xs text-muted-foreground">SSN/ITIN</p>
                          <p className="text-sm">{dependent.ssn || 'N/A'}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-muted-foreground">Date of Birth</p>
                        <p className="text-sm">{dependent.dob || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Relationship</p>
                        <p className="text-sm capitalize">{dependent.relationship?.replace(/_/g, ' ') || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Income Details */}
        {filing.income_details && typeof filing.income_details === 'object' && Object.keys(filing.income_details).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Income Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {Object.entries(filing.income_details)
                  .filter(([key, value]) => {
                    if (typeof value === 'object' && value !== null) return false;
                    return true;
                  })
                  .map(([key, value]) => (
                    <div key={key}>
                      <p className="text-sm text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className="font-medium">
                        {typeof value === 'number' ? `$${value.toFixed(2)}` : value || 'N/A'}
                      </p>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Deductions */}
        {filing.deductions && typeof filing.deductions === 'object' && Object.keys(filing.deductions).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Deductions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {Object.entries(filing.deductions)
                  .filter(([key, value]) => {
                    if (key === 'documents') return false;
                    if (key === 'otherDocuments') return false;
                    if (typeof value === 'object' && value !== null) return false;
                    return true;
                  })
                  .map(([key, value]) => (
                    <div key={key}>
                      <p className="text-sm text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className="font-medium">
                        {typeof value === 'number' ? `$${value.toFixed(2)}` : value || 'N/A'}
                      </p>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Uploaded Documents */}
        {documents.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Uploaded Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {documents.map((doc) => {
                  const fileTypeLabel = doc.file_type.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
                  
                  return (
                    <div key={doc.id} className="flex items-center justify-between p-3 border rounded hover:border-primary transition-colors">
                      <div className="flex items-center gap-3 flex-1">
                        <FileText className="h-5 w-5 text-primary" />
                        <div>
                          <p className="text-xs text-muted-foreground capitalize">{fileTypeLabel}</p>
                          <p className="text-sm font-medium">{doc.file_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ''}
                            {doc.file_size ? ` • ${(doc.file_size / 1024).toFixed(0)} KB` : ''}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewDocument(doc.id, doc.file_url, doc.file_name, doc.mime_type)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Other Documents Uploaded by Client */}
        {((filing?.deductions?.otherDocuments && filing.deductions.otherDocuments.length > 0) ||
          (filing?.personal_info?.otherDocuments && filing.personal_info.otherDocuments.length > 0)) && (
          <Card>
            <CardHeader>
              <CardTitle>Other Documents</CardTitle>
              <p className="text-sm text-muted-foreground">Additional documents uploaded by client</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(filing.deductions?.otherDocuments || filing.personal_info?.otherDocuments || []).map((doc, index) => (
                  <div key={doc.id || index} className="flex items-center justify-between p-3 border rounded hover:border-primary transition-colors">
                    <div className="flex items-center gap-3 flex-1">
                      <FileText className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm font-medium">{doc.file_name || doc.filename}</p>
                        <p className="text-xs text-muted-foreground">
                          {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ''}
                          {doc.size ? ` • ${(doc.size / 1024).toFixed(0)} KB` : ''}
                          {doc.file_size ? ` • ${(doc.file_size / 1024).toFixed(0)} KB` : ''}
                        </p>
                      </div>
                    </div>
                    {doc.file_url && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewDocument(doc.id, doc.file_url, doc.file_name || doc.filename, doc.mime_type, false, true)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Agent Documents */}
        {filing.agent_documents && filing.agent_documents.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Documents from Agent ({filing.agent_documents.length})</CardTitle>
              <p className="text-sm text-muted-foreground">Documents uploaded by agent for client</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {filing.agent_documents.map((doc, index) => (
                  <div key={doc.id || index} className="flex items-center justify-between p-3 border rounded hover:border-primary transition-colors">
                    <div className="flex items-center gap-3 flex-1">
                      <FileText className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-sm font-medium">{doc.name || doc.filename}</p>
                        <p className="text-xs text-muted-foreground">
                          {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ''}
                          {doc.size ? ` • ${(doc.size / 1024).toFixed(0)} KB` : ''}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewDocument(doc.id, doc.file_url, doc.name || doc.filename, doc.mime_type, true)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        {filing.notes && (
          <Card>
            <CardHeader>
              <CardTitle>Agent Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap">{filing.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Document Preview Modal */}
        <DocumentPreviewModal
          isOpen={previewModal.isOpen}
          onClose={() => setPreviewModal({ ...previewModal, isOpen: false })}
          documentId={previewModal.documentId}
          fileUrl={previewModal.fileUrl}
          fileName={previewModal.fileName}
          mimeType={previewModal.mimeType}
          isAgentDocument={previewModal.isAgentDocument}
          filingId={filingId}
          allowDownload={true}
        />
      </div>
    </DashboardLayout>
  );
}
